import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, WorkflowListItem, Organization, MigrationSummary } from '../lib/api';
import { ArrowRight, CheckCircle, Loader, Search, GitBranch, XCircle } from 'lucide-react';

export const WorkflowMigration: React.FC = () => {
  const { token } = useAdminAuth();
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOrgId, setFilterOrgId] = useState<string>('');

  // Migration form state
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowListItem | null>(null);
  const [targetOrgId, setTargetOrgId] = useState<string>('');
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [includeKBs, setIncludeKBs] = useState(true);
  const [includeTools, setIncludeTools] = useState(true);
  const [includeComponents, setIncludeComponents] = useState(true);
  const [includeTestScenarios, setIncludeTestScenarios] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [formError, setFormError] = useState('');
  const [migrationResult, setMigrationResult] = useState<{ success: boolean; message: string; summary?: MigrationSummary } | null>(null);

  const api = new AdminAPI(token!);

  const loadData = async () => {
    try {
      setLoading(true);
      const [workflowsData, orgsData] = await Promise.all([
        api.listAllWorkflows(filterOrgId || undefined),
        api.getOrganizations(0, 1000)
      ]);
      setWorkflows(workflowsData);
      setOrganizations(orgsData);
      setError('');
    } catch (err) {
      const why = err instanceof Error ? err.message : '';
      setError(`Couldn't load agents and organizations. Retry, or check the API is reachable.${why ? ` (${why})` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filterOrgId]);

  const targetOrganization = organizations.find(o => o.id === targetOrgId);

  const handleMigrate = async () => {
    setFormError('');
    if (!selectedWorkflow || !targetOrgId || !targetOrganization) {
      setFormError('Choose a source agent and a target organization first.');
      return;
    }

    if (selectedWorkflow.organization_id === targetOrgId) {
      setFormError('The target organization must be different from the one the agent is in now.');
      return;
    }

    const included = [
      includeTools && 'tools',
      includeKBs && 'knowledge bases and documents',
      includeComponents && 'UI components',
      includeTestScenarios && 'test scenarios',
    ].filter((x): x is string => Boolean(x));
    const finalName = newWorkflowName || selectedWorkflow.name;
    const extras = included.length
      ? `, plus its ${included.join(', ')}`
      : ' only (no tools, knowledge bases, components or test scenarios)';
    const confirmed = window.confirm(
      `Copy "${selectedWorkflow.name}" to ${targetOrganization.name}?\n\n` +
      `This creates a new agent called "${finalName}" in ${targetOrganization.name} ` +
      `with its conversation states and transitions${extras}. ` +
      `The original in ${selectedWorkflow.organization_name} stays where it is.`
    );
    if (!confirmed) return;

    try {
      setMigrating(true);
      setMigrationResult(null);

      const result = await api.migrateWorkflow({
        source_workflow_id: selectedWorkflow.id,
        source_organization_id: selectedWorkflow.organization_id,
        target_organization_id: targetOrgId,
        new_workflow_name: newWorkflowName || undefined,
        include_knowledge_bases: includeKBs,
        include_tools: includeTools,
        include_components: includeComponents,
        include_test_scenarios: includeTestScenarios
      });

      setMigrationResult({
        success: result.success,
        message: result.message,
        summary: result.summary || undefined
      });

      // Refresh the agent list
      await loadData();

      // Reset form
      setSelectedWorkflow(null);
      setTargetOrgId('');
      setNewWorkflowName('');
    } catch (err) {
      setMigrationResult({
        success: false,
        message: err instanceof Error
          ? `Couldn't copy the agent. ${err.message}`
          : "Couldn't copy the agent. Try again, or check the API logs."
      });
    } finally {
      setMigrating(false);
    }
  };

  const filteredWorkflows = workflows.filter(w =>
    w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.organization_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const emptyMessage = workflows.length === 0 && !filterOrgId
    ? 'No agents have been created in any organization yet.'
    : 'No agents match this search or organization filter. Clear the filters to see all.';

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <GitBranch className="w-8 h-8 text-blue-600" />
          Agent Migration
        </h1>
        <p className="mt-2 text-gray-600">
          Copy an agent and its tools, knowledge bases and components into another organization. The original stays where it is.
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={loadData}
            className="shrink-0 px-3 py-1 rounded-lg border border-red-300 bg-white text-sm text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {migrationResult && (
        <div className={`mb-6 border px-6 py-4 rounded-xl ${
          migrationResult.success
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          <div className="flex items-start gap-3">
            {migrationResult.success ? (
              <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-500" />
            )}
            <div className="flex-1">
              <p className="font-semibold">{migrationResult.message}</p>
              {migrationResult.summary && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="font-medium">States created</div>
                    <div>{migrationResult.summary.states_created}</div>
                  </div>
                  <div>
                    <div className="font-medium">Transitions created</div>
                    <div>{migrationResult.summary.transitions_created}</div>
                  </div>
                  <div>
                    <div className="font-medium">Tools created</div>
                    <div>{migrationResult.summary.tools_created}</div>
                  </div>
                  <div>
                    <div className="font-medium">Knowledge bases created</div>
                    <div>{migrationResult.summary.knowledge_bases_created}</div>
                  </div>
                  <div>
                    <div className="font-medium">Documents created</div>
                    <div>{migrationResult.summary.kb_documents_created}</div>
                  </div>
                  <div>
                    <div className="font-medium">Components linked</div>
                    <div>{migrationResult.summary.components_linked}</div>
                  </div>
                  <div>
                    <div className="font-medium">Test scenarios created</div>
                    <div>{migrationResult.summary.test_scenarios_created}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: agent selection */}
        <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Select the source agent</h2>

          {/* Filters */}
          <div className="space-y-3 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search agents or organizations…"
                aria-label="Search agents or organizations"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <select
              value={filterOrgId}
              onChange={(e) => setFilterOrgId(e.target.value)}
              aria-label="Filter by organization"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All organizations</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>

          {/* Agent list */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                <Loader className="w-6 h-6 animate-spin text-blue-600" /> Loading agents…
              </div>
            ) : filteredWorkflows.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{emptyMessage}</p>
            ) : (
              filteredWorkflows.map(workflow => (
                <button
                  key={workflow.id}
                  onClick={() => {
                    setSelectedWorkflow(workflow);
                    setNewWorkflowName('');
                    setFormError('');
                  }}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    selectedWorkflow?.id === workflow.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="font-semibold text-gray-900">{workflow.name}</div>
                  <div className="text-sm text-gray-600 mt-1">{workflow.organization_name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {workflow.states_count} conversation state{workflow.states_count === 1 ? '' : 's'}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: copy configuration */}
        <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Configure the copy</h2>

          {selectedWorkflow ? (
            <div className="space-y-4">
              {/* Selected agent */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-600 font-medium mb-1">Source agent</div>
                <div className="font-semibold text-gray-900">{selectedWorkflow.name}</div>
                <div className="text-sm text-gray-600">{selectedWorkflow.organization_name}</div>
              </div>

              {/* Target organization */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target organization *
                </label>
                <select
                  value={targetOrgId}
                  onChange={(e) => { setTargetOrgId(e.target.value); setFormError(''); }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select an organization…</option>
                  {organizations
                    .filter(org => org.id !== selectedWorkflow.organization_id)
                    .map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                </select>
              </div>

              {/* New agent name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New agent name (optional)
                </label>
                <input
                  type="text"
                  value={newWorkflowName}
                  onChange={(e) => setNewWorkflowName(e.target.value)}
                  placeholder={selectedWorkflow.name}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to keep the original name
                </p>
              </div>

              {/* What to copy */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700 mb-2">Copy along with the agent:</div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeTools}
                    onChange={(e) => setIncludeTools(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Tools</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeKBs}
                    onChange={(e) => setIncludeKBs(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Knowledge bases and documents</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeComponents}
                    onChange={(e) => setIncludeComponents(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">UI components</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeTestScenarios}
                    onChange={(e) => setIncludeTestScenarios(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Test scenarios</span>
                </label>
              </div>

              {/* Preview */}
              {targetOrganization && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium text-gray-700">{selectedWorkflow.organization_name}</div>
                      <div className="text-gray-600">{selectedWorkflow.name}</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-700">{targetOrganization.name}</div>
                      <div className="text-gray-600">{newWorkflowName || selectedWorkflow.name}</div>
                    </div>
                  </div>
                </div>
              )}

              {formError && (
                <p className="text-sm text-red-600">{formError}</p>
              )}

              {/* Copy button */}
              <button
                onClick={handleMigrate}
                disabled={!targetOrgId || migrating}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                  !targetOrgId || migrating
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {migrating ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Copying…
                  </>
                ) : (
                  <>
                    <GitBranch className="w-5 h-5" />
                    {targetOrganization ? `Copy to ${targetOrganization.name}` : 'Choose a target organization'}
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <GitBranch className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>Select an agent on the left to begin</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

